import { AUDIT_HOT_DAYS, type AuditArchiveResult, type BackupObject, type BackupsStatus, type DrillReport } from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { ErrorState } from '../components/guidance/error-state';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { ApiError, api } from '../lib/api';

// Admin → Backups (task 27, PLAN §11 "Backups & disaster recovery"): what protects the database and where the
// newest copies are. Restoring is done from the command line by an admin (runbook), never from this screen.

export const Route = createFileRoute('/admin/backups')({ component: BackupsScreen });

const TOUR = {
  id: 'admin-backups',
  steps: [
    {
      target: '[data-tour="backups-layers"]',
      title: 'Three layers',
      body: 'Time Travel restores the database to any minute of the last 30 days. Nightly exports and the export before every production update are independent copies.',
      learnMore: 'backups-and-restore',
    },
    {
      target: '[data-tour="backups-drills"]',
      title: 'Weekly drills',
      body: 'Every week the latest backup is restored into a scratch database and checked, and staging practises undoing an update. Failed checks are listed here.',
      learnMore: 'backups-and-restore',
    },
    {
      target: '[data-tour="backups-audit"]',
      title: 'The audit log archive',
      body: `Entries older than ${AUDIT_HOT_DAYS} days move to a monthly archive, a month at a time. The nightly job does it; Archive now does it immediately.`,
    },
  ],
};

const DAY = 86_400_000;
const when = (ms: number) =>
  new Date(ms).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const size = (n: number) => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
const age = (ms: number, now = Date.now()) => {
  const h = Math.round((now - ms) / 3_600_000);
  return h < 1 ? 'less than an hour ago' : h < 48 ? `${h} hours ago` : `${Math.round(h / 24)} days ago`;
};

function BackupsScreen() {
  useScreenTour(TOUR);
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const q = useQuery({ queryKey: ['admin', 'backups'], queryFn: () => api<BackupsStatus>('/admin/backups') });
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const archive = useMutation({
    mutationFn: () =>
      withReauth(() => api<{ result: AuditArchiveResult; status: BackupsStatus }>('/admin/backups/archive-audit', { body: {} })),
    onSuccess: r => {
      qc.setQueryData(['admin', 'backups'], r.status);
      setMessage({
        kind: 'ok',
        text: r.result.archived
          ? `Archived ${r.result.archived.toLocaleString()} entries in ${r.result.objects.length} file${r.result.objects.length === 1 ? '' : 's'}.${r.result.more ? ' More are due: the next run continues.' : ''}`
          : 'Nothing is due for the archive yet.',
      });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
    },
  });

  if (q.isError)
    return (
      <>
        <PageHeader title="Backups" />
        <ErrorState
          error={q.error}
          title="The backups overview couldn’t be loaded"
          onRetry={() => q.refetch()}
          learnMore="backups-and-restore"
        />
      </>
    );
  if (!q.data) return <PageHeader title="Backups" description="Loading…" />;
  const s = q.data;
  const latest = s.nightly[0];
  const stale = !latest || Date.now() - latest.uploadedAt > 1.5 * DAY;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Backups"
        description={`How the ${s.environment} database is protected, and where the newest copies are. Restores are done from the command line by an admin.`}
      />
      <Tip id="backups-restore" learnMore="backups-and-restore">
        To undo a mistake from the last 30 days, Time Travel is fastest: it restores the database to the minute before. Note the time
        something went wrong.
      </Tip>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      <section aria-labelledby="layers-title" className="space-y-4" data-tour="backups-layers">
        <h2 id="layers-title" className="font-semibold">
          Protection
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <Card title="Time Travel">
            <p>
              Any minute of the last <DataNumber>{s.timeTravelDays}</DataNumber> days, kept by Cloudflare for the database automatically.
            </p>
            <p className="text-muted-foreground">
              Now: <DataNumber className="font-mono text-xs">{new Date().toISOString().slice(0, 16)}Z</DataNumber>
            </p>
          </Card>
          <Card title="Nightly export">
            {latest ? (
              <p>
                Last: <DataNumber>{when(latest.uploadedAt)}</DataNumber> ({age(latest.uploadedAt)}),{' '}
                <DataNumber>{size(latest.size)}</DataNumber>
              </p>
            ) : (
              <p>No nightly export found for this environment yet.</p>
            )}
            {stale && (
              <p role="status" className="text-warning-foreground">
                {latest ? 'The last export is more than a day old: check the backup job in GitHub Actions.' : 'The job runs at 02:17 UTC.'}
              </p>
            )}
          </Card>
          <Card title="Before each update">
            {s.preDeploy[0] ? (
              <p>
                Last: <DataNumber>{when(s.preDeploy[0].uploadedAt)}</DataNumber>, <DataNumber>{size(s.preDeploy[0].size)}</DataNumber>
              </p>
            ) : (
              <p>{s.environment === 'production' ? 'None yet.' : 'Taken for production only, before its database changes.'}</p>
            )}
          </Card>
        </div>
        <Copies title="Nightly exports" items={s.nightly} empty="No nightly exports yet." />
        {s.preDeploy.length > 0 && <Copies title="Exports before updates" items={s.preDeploy} empty="" />}
      </section>

      <section aria-labelledby="drills-title" className="space-y-4" data-tour="backups-drills">
        <h2 id="drills-title" className="font-semibold">
          Drills
        </h2>
        <p className="text-sm">
          Every Sunday a backup is restored into a scratch database and checked, and staging practises undoing an update. A backup only
          counts if it can be restored.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <DrillCard
            title="Restore drill"
            report={s.drills.restore}
            empty={`No restore drill has run for ${s.environment} yet. It runs on Sundays at 03:23 UTC.`}
          />
          <DrillCard
            title="Rollback rehearsal (staging)"
            report={s.drills.rollback}
            empty="No rollback rehearsal has run yet. It runs on Sundays after the restore drill."
          />
        </div>
      </section>

      <section aria-labelledby="audit-title" className="space-y-3" data-tour="backups-audit">
        <h2 id="audit-title" className="font-semibold">
          Audit log archive
        </h2>
        <p className="text-sm">
          <DataNumber>{s.audit.hot.toLocaleString()}</DataNumber> entries in the{' '}
          <Link to="/admin/audit" className="underline">
            audit log
          </Link>
          {s.audit.oldestAt && (
            <>
              , from <DataNumber>{when(s.audit.oldestAt)}</DataNumber>
            </>
          )}
          . Entries from before <DataNumber>{new Date(s.audit.archiveBefore).toISOString().slice(0, 10)}</DataNumber> move to the archive:{' '}
          <DataNumber>{s.audit.due.toLocaleString()}</DataNumber> due.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            setMessage(null);
            archive.mutate();
          }}
          disabled={archive.isPending}
        >
          {archive.isPending ? 'Archiving…' : 'Archive now'}
        </Button>
        <Copies title="Archived months" items={s.auditArchives} empty="Nothing archived yet." />
        <LearnMore slug="backups-and-restore" />
      </section>
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2 rounded-lg border bg-surface p-4 text-sm">
      <h3 className="font-medium">{title}</h3>
      {children}
    </div>
  );
}

/** The newest report of a weekly drill (task 48): passed or failed, when, and each check in words. */
function DrillCard({ title, report, empty }: { title: string; report: DrillReport | null; empty: string }) {
  if (!report)
    return (
      <Card title={title}>
        <p className="text-muted-foreground">{empty}</p>
      </Card>
    );
  const stale = Date.now() - report.finishedAt > 8 * DAY;
  return (
    <Card title={title}>
      <p>
        <span className={report.ok ? 'font-medium text-success-foreground' : 'font-medium text-danger-foreground'}>
          {report.ok ? 'Passed' : 'Failed'}
        </span>{' '}
        <DataNumber>{when(report.finishedAt)}</DataNumber> ({age(report.finishedAt)})
      </p>
      <p className="break-all font-mono text-xs text-muted-foreground">
        <DataText>{report.source}</DataText>
      </p>
      {stale && (
        <p role="status" className="text-warning-foreground">
          The last drill is more than a week old: check the drill job in GitHub Actions.
        </p>
      )}
      <ul className="space-y-1" aria-label={`${title} checks`}>
        {report.checks.map(c => (
          <li key={c.id}>
            <span className={c.ok ? 'text-success-foreground' : 'font-medium text-danger-foreground'}>{c.ok ? 'Pass' : 'Fail'}</span>{' '}
            {c.label}
            <span className="block text-xs text-muted-foreground">
              <DataText>{c.detail}</DataText>
            </span>
          </li>
        ))}
      </ul>
      {report.runUrl?.startsWith('https://github.com/') && (
        <a href={report.runUrl} target="_blank" rel="noopener noreferrer" className="text-sm underline">
          Run details in GitHub Actions
        </a>
      )}
    </Card>
  );
}

function Copies({ title, items, empty }: { title: string; items: BackupObject[]; empty: string }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y rounded-lg border bg-surface text-sm" aria-label={title}>
          {items.map(o => (
            <li key={o.key} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1 break-all font-mono text-xs">{o.key}</span>
              <DataNumber className="text-xs text-muted-foreground">{size(o.size)}</DataNumber>
              <DataNumber className="text-xs text-muted-foreground">{when(o.uploadedAt)}</DataNumber>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
